import { Container } from "@chakra-ui/react/container"
import { Flex } from "@chakra-ui/react/flex"
import { Grid, GridItem } from "@chakra-ui/react/grid"
import { VStack } from "@chakra-ui/react/stack"
import { FC } from "react"

import ButtonLink from "@/components/ButtonLink/ButtonLink"
import Card11 from "@/components/Cards/Card11"
import ErrorMessage from "@/components/ErrorMessage"
import SectionTitle from "@/components/SectionTitle"
import { PostFixed } from "@/types/database"
import { NewspaperOutlineIcon } from "@/components/icons"

interface LatestPostsProps {
  data?: PostFixed[]
  title?: string
}

const EMPTY_POSTS: PostFixed[] = []

const SectionLatestPosts: FC<LatestPostsProps> = ({
  data = EMPTY_POSTS,
  title = "Articles & Case Studies",
}) => {
  // Check if data is an array and has items
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <Container maxW="8xl">
        <VStack
          borderColor="neutral.200"
          mt={6}
          position="relative"
          rounded={"2xl"}
        >
          <ErrorMessage
            message="Please try to refresh the page."
            status="error"
            title="Unable to find any articles available at the moment!"
          />
        </VStack>
      </Container>
    )
  }

  const lastIndex = data.length - 1
  const gridSize =
    data.length <= 3
      ? {
          base: "1fr",
          sm: "1fr",
          md: "repeat(2, minmax(0, 1fr))",
          lg: "repeat(3, minmax(0, 1fr))",
          xl: "repeat(3, minmax(0, 1fr))",
        }
      : {
          base: "1fr",
          sm: "1fr",
          md: "repeat(2, minmax(0, 1fr))",
          lg: "repeat(2, minmax(0, 1fr))",
          xl: "repeat(3, minmax(0, 1fr))",
        }
  return (
    <Flex as="section" flexDirection="column" mb={0} w="full">
      <Flex
        alignItems="stretch"
        bg="bg.paper"
        borderLeft={"1px solid {colors.border}"}
        borderRight={"1px solid {colors.border}"}
        flexDirection="column"
        flexWrap="nowrap"
        justifyContent="flex-start"
        maxW="8xl"
        mx="auto"
        overflow="hidden"
        position="relative"
        rounded="0"
        w="full"
        zIndex={1}
      >
        <SectionTitle
          borderBottom={"1px solid {colors.border}"}
          preface=""
          subTitle=""
          title={title}
        />

        <Grid
          borderBottom={"1px solid {colors.border}"}
          // templateRows={['repeat(6, minmax(360px, 1fr))', 'repeat(2, 360px)']}
          gap={{ base: 0 }}
          maxW="8xl"
          mx="auto"
          templateColumns={gridSize}
          w="full"
        >
          {data.map((post: PostFixed, index: number) => {
            // Only apply span if we have more than one post
            const shouldSpan =
              data.length > 1 && (index === 0 || index === lastIndex)
            const gridCol = shouldSpan ? "span 1 / span 1" : "inherit"

            return (
              <GridItem
                _last={{
                  borderRight:
                    index < 3
                      ? "1px solid {colors.border}"
                      : "1px solid transparent",
                }}
                aspectRatio="16 / 9"
                borderRight={"1px solid {colors.border}"}
                gridColumn={["span 1 / span 1", gridCol, gridCol]}
                key={post.id || `post-${index}`}
              >
                <Card11 imageFit="contain" post={post} />
              </GridItem>
            )
          })}
        </Grid>
        <Flex gap={6} justifyContent="center" py={6}>
          <ButtonLink
            colorPalette={"primary"}
            data-umami-event={"view-all-case-studies"}
            href="/work"
            label="View all Case Studies"
            leadingIcon={<NewspaperOutlineIcon />}
            variant={"subtle"}
          />
        </Flex>
      </Flex>
    </Flex>
  )
}

export default SectionLatestPosts
